import { useEffect, useState } from 'preact/hooks';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Install affordance.
 *
 * Chromium fires `beforeinstallprompt`, so there is a real button. iOS has no such event
 * and never will — the only route is Share → Add to Home Screen, so the flow has to be
 * taught rather than triggered. That matters beyond tidiness: Web Push on iOS only works
 * from an installed home-screen PWA.
 */
export function InstallHint() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    addEventListener('beforeinstallprompt', onPrompt);
    addEventListener('appinstalled', onInstalled);
    return () => {
      removeEventListener('beforeinstallprompt', onPrompt);
      removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const isStandalone =
    (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  if (installed || isStandalone) {
    return <p class="muted">Podcastie is installed on this device.</p>;
  }

  if (deferred) {
    return (
      <button
        type="button"
        class="button"
        onClick={async () => {
          await deferred.prompt();
          await deferred.userChoice;
          setDeferred(null);
        }}
      >
        Install Podcastie
      </button>
    );
  }

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (isIos) {
    return (
      <p class="muted">
        To install on iOS: tap the <strong>Share</strong> button in Safari, then{' '}
        <strong>Add to Home Screen</strong>. Installing is also what enables notifications on iOS.
      </p>
    );
  }

  return (
    <p class="muted">
      Use your browser&rsquo;s <strong>Install app</strong> or <strong>Add to Home Screen</strong>{' '}
      option to install Podcastie.
    </p>
  );
}
